/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import {
  AuthEmailNullReferenceGuard,
  HasAnyRightGuard,
  HasSignUpRightGuard,
  StoreAuthenticationTokenInLocalStorageIfPresentGuard,
} from '@irian-ro/auth-shared-module';
import { OidcIsLoggedInGuard } from '@irian-ro/auth-oidc';
import { ErrorComponent } from '@well/error';
import { LoggedInTermsAndConditionsPageComponent } from './components/logged-in-terms-and-conditions-page/logged-in-terms-and-conditions-page.component';
import { RoutePopupComponent } from './components/route-popup/route-popup.component';
import { RoutePopupFullComponent } from './components/route-popup-full/route-popup-full.component';
import { RightSideComponent } from './components/right-side/right-side.component';
import { Rights } from '@well/rights';
import { noAuthGuard, signupGuard } from '@well/signup';
import { WellWorkflowsModule } from '@well/infra/workflows/definition';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import {
  companyDetailsValidationRouterSegment,
  contactPlatformCompany,
  termsAndConditionsRouterSegment,
} from '@well/navigation';
import { TermsAndConditionsAcceptPageComponent } from './components/terms-and-conditions/terms-and-conditions-accept-page/terms-and-conditions-accept-page.component';
import { CompanyDetailsValidationPageComponent } from './components/company-details-validation-page/company-details-validation-page.component';
import {
  companyDetailsNotValidatedGuard,
  companyDetailsValidatedGuard,
  termsAndConditionsAcceptedGuard,
} from '@well/shared-components';
import { loggedInContextGate } from '@well/shared-providers';
import { GuestTermsAndConditionsPageComponent } from './components/guest-terms-and-conditions-page/guest-terms-and-conditions-page.component';
import { PlaygroundComponent } from './components/playground/playground.component';

const privateRoutes: Routes = [
  {
    path: 'playground',
    component: PlaygroundComponent
  },
  {
    path: '',
    loadChildren: () => import('../../../auth/src/app/app.module').then((m) => m.AppModule),
  },
  {
    path: 'company',
    loadChildren: () => import('../../../company/src/app/app.module').then((m) => m.AppModule),
  },
  {
    path: 'crm',
    loadChildren: () => import('../../../crm/src/app/app.module').then((m) => m.AppModule),
  },
  {
    path: 'delivery',
    loadChildren: () => import('../../../delivery/src/app/app.module').then((m) => m.AppModule),
  },
  {
    path: 'saft',
    loadChildren: () => import('../../../saft/src/app/lazy.routes').then((m) => m.appRoutes),
    data: {
      authorities: [Rights.ROLE_SAFT],
    },
  },
  {
    path: 'system-administration',
    loadChildren: () => import('../../../system-administration/src/app/app.module').then((m) => m.AppModule),
  },
  {
    path: 'admin',
    loadChildren: () => import('@well/infra/admin-panel').then((module) => module.InfraAdminPanelModule),
  }
];

const signupRoutes: Routes = [
  {
    path: 'signup',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'step' },
      {
        path: 'step',
        loadChildren: () => import('@well/signup').then((module) => module.SignupWizardFeatureModule),
        canMatch: [OidcIsLoggedInGuard, loggedInContextGate],
        canActivate: [signupGuard],
        data: { authorities: [Rights.ROLE_SIGNUP] },
        canActivateChild: [HasAnyRightGuard.setConfig({ redirectUrl: 'signup/user-signup' })],
      },
      {
        path: 'user-signup',
        loadChildren: () => import('@well/signup').then((module) => module.SignupUserFeatureModule),
        // logoutResolver is wired INSIDE SignupUserFeatureModule's routing — wiring it here
        // too made it run twice per visit.
        canActivate: [noAuthGuard],
        data: [{ logout: true }],
      }
    ],
  }
];

const publicRoutes: Routes = [
  {
    path: 'error',
    component: ErrorComponent,
  },
  {
    path: '',
    loadChildren: () => import('../../../auth/src/app/public-router.module').then((m) => m.PublicRouterModule),
  },
  {
    path: 'accessdenied',
    component: ErrorComponent,
  },
  {
    path: 'info',

    children: [
      {
        path: '',
        redirectTo: 'gdpr-terms-and-conditions',
        pathMatch: 'full',
      },
      {
        path: 'gdpr-terms-and-conditions',
        component: GuestTermsAndConditionsPageComponent,
      }
    ],
  },

  {
    path: `${contactPlatformCompany}`,
    loadChildren: () =>
      import('@well/infra/platform-company-contact-form').then(
        (module) => module.LibsInfraPlatformCompanyContactFormModule
      ),
  }
];

const routes: Routes = [
  {
    path: '',
    canMatch: [StoreAuthenticationTokenInLocalStorageIfPresentGuard],

    children: [
      {
        // public routes
        path: '',
        children: publicRoutes,
      },
      {
        // signup routes
        path: '',
        children: signupRoutes,
      },
      {
        // private routes — gate pipeline order (requirements §5): token auth → context load/
        // classification → (inner) T&C → company-validation → rights. Only the FIRST gate may
        // ever trigger re-login; everything after redirects in-app.
        path: '',
        canMatch: [OidcIsLoggedInGuard, loggedInContextGate],
        canActivateChild: [
          HasSignUpRightGuard.setConfig({
            signupRights: [Rights.ROLE_SIGNUP],
            redirectUrl: 'signup',
          }),
          HasAnyRightGuard.setConfig({ redirectUrl: 'accessdenied' })
        ],
        canActivate: [AuthEmailNullReferenceGuard],
        children: [
          {
            path: termsAndConditionsRouterSegment,
            component: TermsAndConditionsAcceptPageComponent,
          },
          {
            path: companyDetailsValidationRouterSegment,
            component: CompanyDetailsValidationPageComponent,
            canMatch: [companyDetailsNotValidatedGuard],
          },
          {
            path: '',
            canMatch: [termsAndConditionsAcceptedGuard, companyDetailsValidatedGuard],
            children: privateRoutes,
          }
        ],
      },

      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        redirectTo: 'admin/dashboard',
        pathMatch: 'prefix',
      },
      {
        path: 'gdpr-terms-and-conditions',
        component: LoggedInTermsAndConditionsPageComponent,
      }
    ],
  },
  { path: '**', component: ErrorComponent }
];

export const fullRoutes: Route[] = [
  ...routes,
  {
    path: 'popup',
    outlet: 'popup',
    component: RoutePopupComponent,
    children: routes,
  },
  {
    path: 'popup-full',
    outlet: 'popup-full',
    component: RoutePopupFullComponent,
    children: routes,
  },
  {
    path: 'right-side',
    outlet: 'right-side',
    component: RightSideComponent,
    children: routes,
  },
  {
    path: 'sidebar',
    outlet: 'sidebar',
    component: SidebarComponent,
    children: routes,
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'dashboard',
        },
        ...fullRoutes
      ],
      {
        useHash: false,
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
        scrollOffset: [0, 100],
        bindToComponentInputs: true,
      }
    ),
    WellWorkflowsModule
  ],
  exports: [RouterModule],
})
export class ShellRoutingModule {}
